import {
  API_BASE,
  type CompleteHours,
  type Account,
  type AccountProfile,
  type AccountRole,
  type AccountSessionView,
  type AcceptTeamTermsInput,
  type AdminUpdateProfileInput,
  type ApiErrorBody,
  type ActivityFeed,
  type ApiToken,
  type ApiTokenCreated,
  type AuthState,
  type Availability,
  type Client,
  type ClientStatus,
  type CreateApiTokenInput,
  type CreateAssignmentInput,
  type CreateClientInput,
  type CreateJobInput,
  type CreateJobTypeInput,
  type CreateNoteInput,
  type DirectoryAccount,
  type Job,
  type StudioHoursMonth,
  type JobAssignment,
  type AssignmentHourAdjustment,
  type DeductAssignmentHoursInput,
  type JobNote,
  type JobStatus,
  type JobType,
  type Level,
  type CreateLevelInput,
  type UpdateLevelInput,
  type MemberDetail,
  type NoteEntityType,
  type CreatePaymentInput,
  type MoneyTotals,
  type MyWorkView,
  type NotificationsView,
  type AnnouncementView,
  type CreateAnnouncementInput,
  type UpdateAnnouncementInput,
  type OpsStatus,
  type PortalNotesView,
  type PaymentTransaction,
  type PaymentSheet,
  type PaymentSheetSummary,
  type CreatePaymentSheetInput,
  type UpdateSheetTaskInput,
  type UpdateSheetPlayerInput,
  type ClientBillingStatus,
  type PlayerStatement,
  type WorkView,
  type RevealedProfileFields,
  type SensitiveProfileField,
  type SetAssignmentCompletionInput,
  type SubmitProfileInput,
  type UpdateAssignmentInput,
  type UpdateClientInput,
  type UpdateJobInput,
  type UpdateJobTypeInput,
  type UpdateNoteInput,
  type WhitelistAccountInput,
  type CreateWorkflowInput,
  type CreateWorkflowStepInput,
  type CreateRuntimeInput,
  type DispatchTaskInput,
  type InstantiateWorkflowInput,
  type Runtime,
  type RuntimeCreated,
  type RuntimeTask,
  type RuntimeTaskDetail,
  type UpdateRuntimeInput,
  type UpdateWorkflowInput,
  type UpdateWorkflowStepInput,
  type Workflow,
  type WorkflowStep,
} from '@workflow/contracts'

/** An API failure, carrying the server's own code and words. */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details: unknown
  /** For a rate-limited answer: how long the server asked us to wait, in ms. */
  readonly retryAfterMs: number | null

  constructor(
    status: number,
    body: { code: string; message: string; details?: unknown },
    retryAfterMs: number | null = null,
  ) {
    super(body.message)
    this.code = body.code
    this.status = status
    this.details = body.details
    this.retryAfterMs = retryAfterMs
  }
}

/** The Retry-After header, parsed (seconds or a date) into milliseconds. */
function retryAfterMs(response: Response): number | null {
  const raw = response.headers.get('retry-after')
  if (raw === null) return null
  const seconds = Number(raw)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)
  const at = Date.parse(raw)
  return Number.isNaN(at) ? null : Math.max(0, at - Date.now())
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { accept: 'application/json', ...(init.body ? { 'content-type': 'application/json' } : {}), ...init.headers },
    })
  } catch {
    throw new ApiError(0, { code: 'unreachable', message: 'The Ops API cannot be reached.' })
  }
  const body = (await response.json().catch(() => null)) as unknown
  if (!response.ok) {
    const error = (body as ApiErrorBody | null)?.error
    throw new ApiError(
      response.status,
      error ?? { code: 'internal', message: `The server answered ${response.status}.` },
      retryAfterMs(response),
    )
  }
  return body as T
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })

const put = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) })

const patch = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) })

const del = <T>(path: string) => request<T>(path, { method: 'DELETE' })

/** The sentence to show for a failed request: the server's words when it gave any. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

/**
 * The sentence for a failed *submit*: the server's validation words, most
 * specific first — the field and what is wrong with it. Every form shows
 * this under its button, so a refusal is read where the typing happened.
 */
export function errorSentence(error: unknown): string {
  if (error instanceof ApiError && Array.isArray(error.details)) {
    const first = (error.details as { path?: string; message?: string }[])[0]
    if (first?.message) return [first.path, first.message].filter(Boolean).join(': ')
  }
  return describeError(error)
}

export const api = {
  status: () => request<OpsStatus>('/status'),

  // Sign-in and the account it lands in.
  session: () => request<AuthState>('/auth/session'),
  devSignIn: (email: string) => post<Account>('/auth/dev-sign-in', { email }),
  oneTap: (credential: string) => post<Account>('/auth/one-tap', { credential }),
  signOut: () => post<void>('/auth/sign-out'),
  profile: () => request<AccountProfile | null>('/profile'),
  submitProfile: (input: SubmitProfileInput) => put<AccountProfile>('/profile', input),
  acceptTeamTerms: (input: AcceptTeamTermsInput) => post<{ version: string; issuedOn: string; acceptedAt: string }>('/auth/terms/accept', input),

  // The devices list: this account's live sessions, and the acts that end them.
  sessions: () => request<AccountSessionView[]>('/profile/sessions'),
  revokeSession: (id: string) => post<void>(`/profile/sessions/${id}/revoke`),
  revokeAllSessions: () => post<{ revoked: number }>('/profile/sessions/revoke-all'),

  // Account administration, under /admin. The server refuses these to anyone else.
  members: () => request<DirectoryAccount[]>('/admin/members'),
  member: (id: string) => request<MemberDetail>(`/admin/members/${id}`),
  whitelistMember: (input: WhitelistAccountInput) => post<Account>('/admin/members', input),
  approveMember: (id: string) => post<Account>(`/admin/members/${id}/approve`),
  rejectMember: (id: string) => post<Account>(`/admin/members/${id}/reject`),
  suspendMember: (id: string) => post<Account>(`/admin/members/${id}/suspend`),
  revokeMember: (id: string) => post<Account>(`/admin/members/${id}/revoke`),
  restoreMember: (id: string) => post<Account>(`/admin/members/${id}/restore`),
  changeMemberRole: (id: string, role: AccountRole) => post<Account>(`/admin/members/${id}/role`, { role }),
  setMemberLevel: (id: string, levelId: string) => post<Account>(`/admin/members/${id}/level`, { levelId }),
  updateMemberProfile: (id: string, input: AdminUpdateProfileInput) =>
    patch<MemberDetail>(`/admin/members/${id}/profile`, input),
  revealMemberFields: (id: string, fields: SensitiveProfileField[]) =>
    post<RevealedProfileFields>(`/admin/members/${id}/reveal`, { fields }),
  deleteMember: (id: string) => del<void>(`/admin/members/${id}`),

  // Activity: the audit trail with its identifiers named.
  activity: () => request<ActivityFeed>('/admin/activity'),

  // Clients. Every field is editable mid-flight; status changes are decisions.
  clients: () => request<Client[]>('/clients'),
  client: (id: string) => request<Client>(`/clients/${id}`),
  createClient: (input: CreateClientInput) => post<Client>('/clients', input),
  updateClient: (id: string, input: UpdateClientInput) => patch<Client>(`/clients/${id}`, input),
  setClientStatus: (id: string, status: Extract<ClientStatus, 'active' | 'inactive'>) =>
    post<Client>(`/clients/${id}/status`, { status }),
  archiveClient: (id: string) => post<Client>(`/clients/${id}/archive`),
  restoreClient: (id: string) => post<Client>(`/clients/${id}/restore`),
  deleteClient: (id: string) => del<void>(`/clients/${id}`),

  // Jobs. The ledger and the record; status moves are decisions the machine guards.
  jobTypes: () => request<JobType[]>('/job-types'),
  /** Every kind, living and retired — the Settings pane's own reading. */
  allJobTypes: () => request<JobType[]>('/job-types?include=retired'),
  // The kinds of work, shaped by administrators: add, rename, re-price,
  // retire, restore. A retired kind answers by id but joins no new offering.
  createJobType: (input: CreateJobTypeInput) => post<JobType>('/job-types', input),
  updateJobType: (id: string, input: UpdateJobTypeInput) => patch<JobType>(`/job-types/${id}`, input),
  retireJobType: (id: string) => post<JobType>(`/job-types/${id}/retire`),
  restoreJobType: (id: string) => post<JobType>(`/job-types/${id}/restore`),

  // The levels and what each carries, shaped by administrators.
  levels: () => request<Level[]>('/admin/levels'),
  createLevel: (input: CreateLevelInput) => post<Level>('/admin/levels', input),
  updateLevel: (id: string, input: UpdateLevelInput) => patch<Level>(`/admin/levels/${id}`, input),

  // The workflows: a client's ordered templates of work, and the steps inside
  // them. Reading is staff; shaping is administration; the work they open is
  // a job decision and lives with the other job verbs above.
  workflows: (clientId?: string) =>
    request<Workflow[]>(`/workflows${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''}`),
  workflow: (id: string) => request<Workflow>(`/workflows/${id}`),
  createWorkflow: (input: CreateWorkflowInput) => post<Workflow>('/workflows', input),
  updateWorkflow: (id: string, input: UpdateWorkflowInput) => patch<Workflow>(`/workflows/${id}`, input),
  deleteWorkflow: (id: string) => del<void>(`/workflows/${id}`),
  addWorkflowStep: (workflowId: string, input: CreateWorkflowStepInput) =>
    post<WorkflowStep>(`/workflows/${workflowId}/steps`, input),
  updateWorkflowStep: (workflowId: string, stepId: string, input: UpdateWorkflowStepInput) =>
    patch<WorkflowStep>(`/workflows/${workflowId}/steps/${stepId}`, input),
  removeWorkflowStep: (workflowId: string, stepId: string) =>
    post<void>(`/workflows/${workflowId}/steps/${stepId}/remove`),
  reorderWorkflowSteps: (workflowId: string, ids: string[]) =>
    post<WorkflowStep[]>(`/workflows/${workflowId}/steps/reorder`, { ids }),
  jobs: () => request<Job[]>('/jobs'),
  studioHours: (month: string) => request<StudioHoursMonth>(`/jobs/hours-by-day?month=${encodeURIComponent(month)}`),
  job: (id: string) => request<Job>(`/jobs/${id}`),
  createJob: (input: CreateJobInput) => post<Job>('/jobs', input),
  // A client's template, opened: one job per step, each editable afterwards.
  instantiateWorkflow: (input: InstantiateWorkflowInput) => post<Job[]>('/jobs/from-workflow', input),
  updateJob: (id: string, input: UpdateJobInput) => patch<Job>(`/jobs/${id}`, input),
  setJobStatus: (id: string, status: JobStatus) => post<Job>(`/jobs/${id}/status`, { status }),
  scheduleJob: (id: string, at: string) => post<Job>(`/jobs/${id}/schedule`, { at }),
  unscheduleJob: (id: string) => del<Job>(`/jobs/${id}/schedule`),
  duplicateJob: (id: string) => post<Job>(`/jobs/${id}/duplicate`, {}),
  archiveJob: (id: string) => post<Job>(`/jobs/${id}/archive`, {}),
  restoreJob: (id: string) => post<Job>(`/jobs/${id}/restore`, {}),
  deleteJob: (id: string) => del<void>(`/jobs/${id}`),
  paymentSheets: () => request<PaymentSheetSummary[]>('/payment-sheets'),
  paymentSheet: (id: string) => request<PaymentSheet>(`/payment-sheets/${id}`),
  createPaymentSheet: (input: CreatePaymentSheetInput) => post<PaymentSheet>('/payment-sheets', input),
  refreshPaymentSheet: (id: string) => post<PaymentSheet>(`/payment-sheets/${id}/refresh`),
  sharePaymentSheet: (id: string) => post<PaymentSheet>(`/payment-sheets/${id}/share`),
  archivePaymentSheet: (id: string) => post<void>(`/payment-sheets/${id}/archive`),
  setSheetRate: (id: string, jobId: string, input: UpdateSheetTaskInput) => patch<PaymentSheet>(`/payment-sheets/${id}/tasks/${jobId}`, input),
  setSheetBilling: (id: string, clientKey: string, status: ClientBillingStatus) =>
    patch<PaymentSheet>(`/payment-sheets/${id}/clients/${clientKey || 'internal'}`, { status }),
  updateSheetPlayer: (id: string, playerId: string, input: UpdateSheetPlayerInput) => patch<PaymentSheet>(`/payment-sheets/${id}/players/${playerId}`, input),
  resendStatement: (id: string, playerId: string) => post<PaymentSheet>(`/payment-sheets/${id}/players/${playerId}/resend`),
  myStatements: () => request<PlayerStatement[]>('/portal/statements'),
  acceptStatement: (sheetId: string) => post<PlayerStatement>(`/portal/statements/${sheetId}/accept`),
  disputeStatement: (sheetId: string, reason: string) => post<PlayerStatement>(`/portal/statements/${sheetId}/dispute`, { reason }),


  assignments: (jobId: string) => request<JobAssignment[]>(`/jobs/${jobId}/assignments`),
  assignPlayer: (jobId: string, input: CreateAssignmentInput) => post<JobAssignment>(`/jobs/${jobId}/assignments`, input),
  updateAssignment: (jobId: string, assignmentId: string, input: UpdateAssignmentInput) =>
    patch<JobAssignment>(`/jobs/${jobId}/assignments/${assignmentId}`, input),
  deductAssignmentHours: (jobId: string, assignmentId: string, input: DeductAssignmentHoursInput) =>
    post<JobAssignment>(`/jobs/${jobId}/assignments/${assignmentId}/hours-adjustment`, input),
  assignmentHourAdjustments: (jobId: string, assignmentId: string) =>
    request<AssignmentHourAdjustment[]>(`/jobs/${jobId}/assignments/${assignmentId}/hour-adjustments`),
  setAssignmentCompletion: (jobId: string, assignmentId: string, input: SetAssignmentCompletionInput) =>
    post<JobAssignment>(`/jobs/${jobId}/assignments/${assignmentId}/completion`, input),

  // The money ledger. What moved, in and out — append-only, voided, never edited.
  payments: (filter: { jobId?: string; assignmentId?: string; clientId?: string; playerId?: string } = {}) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(filter)) if (value) params.set(key, value)
    const query = params.toString()
    return request<PaymentTransaction[]>(`/payments${query ? `?${query}` : ''}`)
  },
  paymentTotals: (filter: { jobId?: string; clientId?: string; playerId?: string } = {}) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(filter)) if (value) params.set(key, value)
    return request<MoneyTotals[]>(`/payments/summary?${params.toString()}`)
  },
  recordPayment: (input: CreatePaymentInput) => post<PaymentTransaction>('/payments', input),
  voidPayment: (id: string, reason: string) => post<PaymentTransaction>(`/payments/${id}/void`, { reason }),

  // The work record: one person's rows, read whole by staff and redacted by
  // the portal — the portal builds its own answers.
  myWork: () => request<MyWorkView>('/portal/work'),
  myHourAdjustments: (assignmentId: string) =>
    request<AssignmentHourAdjustment[]>(`/portal/assignments/${assignmentId}/hour-adjustments`),
  portalNotes: () => request<PortalNotesView>('/portal/notes'),
  memberWork: (id: string) => request<WorkView>(`/admin/members/${id}/work`),
  // The player's own verbs: hours as they go, the part finished, a note to the studio.
  logHours: (assignmentId: string, hours: number) =>
    post<void>(`/portal/assignments/${assignmentId}/hours`, { hours }),
  setMyHours: (assignmentId: string, hours: number, expectedHours: number) =>
    put<void>(`/portal/assignments/${assignmentId}/hours`, { hours, expectedHours }),
  completeMyAssignment: (assignmentId: string, hours: CompleteHours = 'logged') =>
    post<void>(`/portal/assignments/${assignmentId}/complete`, { hours }),
  addMyNote: (assignmentId: string, body: string) =>
    post<JobNote>(`/portal/assignments/${assignmentId}/notes`, { body }),
  // The clock: the person's own word on whether they are taking work.
  setAvailability: (availability: Availability) => post<Account>('/portal/availability', { availability }),

  // Notes. One thread per entity; visibility is the access decision.
  notes: (entityType: NoteEntityType, entityId: string) =>
    request<JobNote[]>(`/notes?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`),
  addNote: (input: CreateNoteInput) => post<JobNote>('/notes', input),
  updateNote: (id: string, input: UpdateNoteInput) => patch<JobNote>(`/notes/${id}`, input),

  // The bell: the words that traveled to this account, and the acts that mark them heard.
  notifications: () => request<NotificationsView>('/notifications'),
  markNotificationRead: (id: string) => post<void>(`/notifications/${id}/read`),
  markAllNotificationsRead: () => post<{ read: number }>('/notifications/read-all'),

  // Announcements: everyone reads; administrators write.
  announcements: () => request<AnnouncementView[]>('/announcements'),
  markAnnouncementSeen: (id: string) => post<void>(`/announcements/${id}/seen`),
  voteAnnouncement: (id: string, optionId: string) => post<AnnouncementView>(`/announcements/${id}/vote`, { optionId }),
  closeAnnouncementPoll: (id: string) => post<AnnouncementView>(`/admin/announcements/${id}/poll/close`),
  allAnnouncements: () => request<AnnouncementView[]>('/admin/announcements'),
  createAnnouncement: (input: CreateAnnouncementInput) => post<AnnouncementView>('/admin/announcements', input),
  updateAnnouncement: (id: string, input: UpdateAnnouncementInput) => patch<AnnouncementView>(`/admin/announcements/${id}`, input),
  archiveAnnouncement: (id: string) => post<AnnouncementView>(`/admin/announcements/${id}/archive`),
  restoreAnnouncement: (id: string) => post<AnnouncementView>(`/admin/announcements/${id}/restore`),
  pinAnnouncement: (id: string) => post<AnnouncementView>(`/admin/announcements/${id}/pin`),
  unpinAnnouncement: (id: string) => del<AnnouncementView>(`/admin/announcements/${id}/pin`),

  // Service tokens. The machine passport; the secret is read exactly once.
  apiTokens: () => request<ApiToken[]>('/admin/tokens'),
  createApiToken: (input: CreateApiTokenInput) => post<ApiTokenCreated>('/admin/tokens', input),
  revokeApiToken: (id: string) => post<ApiToken>(`/admin/tokens/${id}/revoke`),

  // Runtimes — the machines that execute the studio's work, and the dispatch
  // queue their tasks open from. The desk is administrators'; dispatching is
  // staff; the machines themselves walk their own surface, not this client.
  runtimes: () => request<Runtime[]>('/admin/runtimes'),
  createRuntime: (input: CreateRuntimeInput) => post<RuntimeCreated>('/admin/runtimes', input),
  updateRuntime: (id: string, input: UpdateRuntimeInput) =>
    request<Runtime>(`/admin/runtimes/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  retireRuntime: (id: string) => post<Runtime>(`/admin/runtimes/${id}/retire`),
  jobDispatch: (jobId: string) => request<{ tasks: RuntimeTaskDetail[] }>(`/jobs/${jobId}/dispatch`),
  dispatchJobTask: (jobId: string, input: DispatchTaskInput) => post<RuntimeTask>(`/jobs/${jobId}/dispatch`, input),
  cancelJobTask: (jobId: string, taskId: string) => post<RuntimeTask>(`/jobs/${jobId}/dispatch/${taskId}/cancel`),
}
